import { FC, memo, ReactNode, useCallback, useState } from 'react'

import { useRouter } from 'next/router'

import { PageLoader } from 'components/page-loader'
import { StatusScreen } from 'components/status-screen'
import { useLinkStatus } from 'hooks/subdelegated/useLinkStatus'
import { DataLayerProps } from 'models/data-layer'
import { SubdelegatedRole } from 'models/interface/subdelegated'
import { pushDataLayer } from 'utils/push-data-layer'

import { Confirmation } from './confirmation'

export type { GuardStatus } from 'hooks/subdelegated/useLinkStatus'

export type InviteGuardData = {
  role?: SubdelegatedRole | null
  email?: string
  lpDocument?: string
  code: string
}

type Props = {
  children: (data: InviteGuardData) => ReactNode
}

const expiredCopy = {
  title: 'Ops, link inválido!',
  description: 'Por segurança, este link de convite expirou. Solicite um novo convite para continuar.'
}

const notFoundCopy = {
  title: 'Convite não encontrado',
  description: 'Não encontramos esse convite. Verifique o link recebido por e-mail ou solicite um novo convite.'
}

const invalidRoleCopy = {
  title: 'Convite inválido',
  description: (
    <>
      Não é possível seguir com o cadastro. Identificamos que você possui um vínculo ativo como Responsável em um
      parceiro.
      <br />
      <br />
      Orientamos que contate o seu consultor CashMe para mais informações.
    </>
  )
}

const errorCopy = {
  title: 'Não foi possível carregar seu convite'
}

export const InviteGuard: FC<Props> = memo(({ children }) => {
  const router = useRouter()
  const [accepted, setAccepted] = useState(false)

  const { convite: tokenParam } = router.query
  const token = Array.isArray(tokenParam) ? tokenParam[0] : tokenParam

  const { status, role, email, lpDocument, errorMessage, retry } = useLinkStatus(token, router.isReady)

  const handleExit = useCallback(() => {
    router.push('/')
  }, [router])

  const handleExitClick = useCallback(
    (dataLayerLabel: string) => {
      const dataLayer: DataLayerProps = {
        category: 'Clique',
        action: 'Convite Substabelecido',
        label: `${dataLayerLabel} - Sair`
      }
      pushDataLayer(dataLayer)
      handleExit()
    },
    [handleExit]
  )

  const handleRetryClick = useCallback(() => {
    pushDataLayer({
      category: 'Clique',
      action: 'Convite Substabelecido',
      label: 'Erro ao Carregar Convite - Tentar novamente'
    })
    retry()
  }, [retry])

  const handleAccept = useCallback(() => {
    pushDataLayer({
      category: 'Clique',
      action: 'Convite Substabelecido',
      label: 'Confirmar Participação - Aceitar convite'
    })
    setAccepted(true)
  }, [])

  const handleCancel = useCallback(() => {
    pushDataLayer({
      category: 'Clique',
      action: 'Convite Substabelecido',
      label: 'Confirmar Participação - Cancelar'
    })
    handleExit()
  }, [handleExit])

  if (status === 'loading') return <PageLoader />

  if (status === 'expired' || status === 'alreadyUsed') {
    const dataLayerLabel = status === 'alreadyUsed' ? 'Convite Já Utilizado' : 'Convite Expirado'
    return (
      <StatusScreen
        tone="warning"
        title={expiredCopy.title}
        description={expiredCopy.description}
        actionsLayout="responsive"
        actions={[
          {
            label: 'Sair',
            variant: 'solid',
            onClick: () => handleExitClick(dataLayerLabel)
          }
        ]}
      />
    )
  }

  if (status === 'notFound') {
    return (
      <StatusScreen
        tone="warning"
        title={notFoundCopy.title}
        description={notFoundCopy.description}
        actionsLayout="responsive"
        actions={[
          {
            label: 'Sair',
            variant: 'solid',
            onClick: () => handleExitClick('Convite Não Encontrado')
          }
        ]}
      />
    )
  }

  if (status === 'invalidRole') {
    return (
      <StatusScreen
        tone="warning"
        title={invalidRoleCopy.title}
        description={invalidRoleCopy.description}
        actionsLayout="responsive"
        actions={[
          {
            label: 'Sair',
            variant: 'solid',
            onClick: () => handleExitClick('Convite Inválido')
          }
        ]}
      />
    )
  }

  if (status === 'error') {
    return (
      <StatusScreen
        tone="danger"
        title={errorCopy.title}
        description={errorMessage}
        actionsLayout="responsive"
        actions={[
          {
            label: 'Sair',
            variant: 'solid',
            color: 'secondary',
            onClick: () => handleExitClick('Erro ao Carregar Convite')
          },
          {
            label: 'Tentar novamente',
            variant: 'solid',
            onClick: handleRetryClick
          }
        ]}
      />
    )
  }

  if (status === 'hasLink' && !accepted) {
    return <Confirmation role={role} onAccept={handleAccept} onCancel={handleCancel} />
  }

  return <>{children({ role, email, lpDocument, code: token ?? '' })}</>
})

InviteGuard.displayName = 'InviteGuard'
